import { Avatar, Box, Chip, CircularProgress, Divider, Typography, IconButton, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import KanbanInput from '../KanbanInput';
import KanbanPriorityAutoComplete from '../KanbanPriorityAutoComplete';
import CreateOrEditKanbanPriorityModal from '../CreateOrEditKanbanPriorityModal';
import KanbanUsersAutocomplete from '../KanbanUsersAutoComplete';
import KanbanContactsAutoComplete from '../KanbanContactsAutoComplete';
import ContactAutoComplete from '../ContactAutoComplete';
import CreateOrEditKanbanTagModal from '../CreateOrEditKanbanTagModal';
import { useEvent } from '../../hooks/useEvent';
import KanbanTagsNewAutoComplete from '../KanbanTagsNewAutoComplete';
import FollowersContactsAutoComplete from '../FollowersContactsAutoComplete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import EditIcon from '@mui/icons-material/Edit';
import KanbanComments from '../KanbanCardCommentsSectionNew';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Componentes estilizados com espaçamentos otimizados, mas mantendo estilo refinado
const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1.75),
  backgroundColor: theme.palette.mode === 'dark' ? '#1b212c' : '#ffffff',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark'
      ? '0 6px 20px rgba(0,0,0,0.25)'
      : '0 6px 20px rgba(0,0,0,0.1)',
  },
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  position: 'relative',
  padding: theme.spacing(0.75),
  borderRadius: theme.spacing(0.75),
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha('#000', 0.15)
      : alpha('#f5f5f5', 0.5),
  },
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: '8px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  height: 24,
  marginRight: theme.spacing(0.75),
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  border: `1px solid ${theme.palette.mode === 'dark'
    ? alpha('#fff', 0.08)
    : alpha('#000', 0.05)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
}));

// Styled components para a timeline compacta mas estilizada
const TimelineSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha('#111924', 0.8)
    : alpha('#f8fafc', 0.8),
  backgroundImage: theme.palette.mode === 'dark'
    ? 'linear-gradient(to bottom, rgba(30,40,60,0.4) 0%, rgba(20,30,50,0.8) 100%)'
    : 'linear-gradient(to bottom, rgba(255,255,255,0.9) 0%, rgba(240,245,250,0.7) 100%)',
  borderRadius: 12,
  border: `1px solid ${theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.08)
    : alpha('#000000', 0.08)}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 16px rgba(0,0,0,0.3)'
    : '0 4px 16px rgba(0,0,0,0.05)',
  position: 'relative',
  overflow: 'hidden',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: theme.palette.mode === 'dark'
      ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.8)} 0%, ${alpha(theme.palette.warning.main, 0.8)} 33%, ${alpha(theme.palette.success.main, 0.8)} 66%, ${alpha(theme.palette.info.main, 0.8)} 100%)`
      : `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 33%, ${theme.palette.success.main} 66%, ${theme.palette.info.main} 100%)`,
  }
}));

const TimelineContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1.5),
  position: 'relative',
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  }
}));

const TimelineConnector = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 20,
  left: '5%',
  right: '5%',
  height: 2,
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.8)} 0%, ${alpha(theme.palette.warning.main, 0.8)} 33%, ${alpha(theme.palette.success.main, 0.8)} 66%, ${alpha(theme.palette.info.main, 0.8)} 100%)`
    : `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 33%, ${theme.palette.success.main} 66%, ${theme.palette.info.main} 100%)`,
  opacity: 0.7,
  zIndex: 1,
  boxShadow: '0 0 4px rgba(0,0,0,0.15)',
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  }
}));

const TimelineItem = styled(Box)(({ theme, active = false, color = 'primary' }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  zIndex: 2,
  flex: 1,
  opacity: active ? 1 : 0.75,
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2.5),
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 14,
      top: 20,
      bottom: -8,
      width: 2,
      backgroundColor: alpha(theme.palette[color].main, 0.4),
    },
    '&:last-child::before': {
      display: 'none',
    }
  }
}));

const TimelineIcon = styled(Box)(({ theme, color = 'primary', active = false }) => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  backgroundColor: active ? theme.palette[color].main : alpha(theme.palette[color].main, 0.15),
  color: active ? '#ffffff' : theme.palette[color].main,
  border: `2px solid ${active ? theme.palette[color].main : alpha(theme.palette[color].main, 0.3)}`,
  boxShadow: active ? `0 0 10px ${alpha(theme.palette[color].main, 0.5)}` : 'none',
  flexShrink: 0,
}));

const TimelineText = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(0.75),
  [theme.breakpoints.down('lg')]: {
    marginTop: 0,
    textAlign: 'left',
  }
}));

const TimelineLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.6875rem',
  fontWeight: 700,
  color: theme.palette.text.secondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: theme.spacing(0.25),
}));

const TimelineValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
}));

const AuthorSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(1),
  borderRadius: 8,
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha('#000', 0.25)
    : alpha('#f8fafc', 0.8),
  border: `1px solid ${theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.05)
    : alpha('#000000', 0.05)}`,
  boxShadow: theme.palette.mode === 'dark'
    ? 'inset 0 1px 3px rgba(0,0,0,0.2)'
    : 'inset 0 1px 3px rgba(0,0,0,0.05)',
}));

const AuthorAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  boxShadow: `0 0 0 2px ${theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.08)
    : alpha('#000000', 0.05)}`,
}));

const AuthorInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const AuthorLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.625rem',
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.15em',
  lineHeight: 1.2,
}));

const AuthorName = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.3,
}));

const TimelineTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
  letterSpacing: '0.05em',
  textAlign: 'center',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -4,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 50,
    height: 2,
    background: theme.palette.mode === 'dark'
      ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.6)} 0%, ${alpha(theme.palette.warning.main, 0.6)} 50%, ${alpha(theme.palette.success.main, 0.6)} 100%)`
      : `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 50%, ${theme.palette.success.main} 100%)`,
    borderRadius: 1,
  },
  [theme.breakpoints.down('lg')]: {
    textAlign: 'left',
    marginBottom: theme.spacing(0.75),
    '&::after': {
      left: 0,
      transform: 'none',
      width: 30,
    }
  }
}));

// Campo editável compacto com estilo refinado
const EditableField = styled(Box)(({ theme, loading }) => ({
  maxWidth: '100%',
  cursor: loading ? 'default' : 'pointer',
  transition: 'all 0.2s ease',
  padding: theme.spacing(0.75),
  borderRadius: theme.shape.borderRadius,
  border: '1px solid transparent',
  position: 'relative',
  '&:hover': loading ? {} : {
    border: `1px solid ${alpha(theme.palette.primary.main, 0.6)}`,
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.04),
    '& .edit-icon': {
      opacity: 1,
    },
  },
}));

// Título estilizado compacto com estilo refinado
const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5),
}));

const Title = styled(Typography)(({ theme, loading }) => ({
  margin: 0,
  color: theme.palette.mode === 'dark' ?
    (loading ? '#2f3641' : '#ffffff') :
    (loading ? '#b2b2b2' : '#000000'),
  transition: 'color 0.2s ease',
  '&:hover + .edit-icon': {
    opacity: 1,
  },
}));

const StyledEditIcon = styled(EditIcon)(({ theme }) => ({
  fontSize: '0.875rem',
  opacity: 0,
  marginLeft: theme.spacing(0.75),
  transition: 'opacity 0.2s ease',
  color: theme.palette.primary.main,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: 0,
    width: 35,
    height: 2,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

// Divider estilizado compacto
const CompactDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(1.5, 0),
  opacity: 0.6,
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)'
    : 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
  height: 1,
}));

// Descrição estilizada
const DescriptionBox = styled(Box)(({ theme, isEmpty }) => ({
  padding: theme.spacing(1),
  borderRadius: 6,
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha('#000', isEmpty ? 0.1 : 0.15)
    : alpha('#000', isEmpty ? 0.02 : 0.03),
  minHeight: 30,
  border: `1px solid ${theme.palette.mode === 'dark'
    ? alpha('#fff', 0.03)
    : alpha('#000', 0.03)}`,
}));

// Estilos para texto da descrição
const DescriptionText = styled(Typography)(({ theme, empty }) => ({
  fontSize: '0.875rem',
  lineHeight: 1.5,
  color: empty ? alpha(theme.palette.text.primary, 0.5) : theme.palette.text.primary,
  fontStyle: empty ? 'italic' : 'normal',
}));

// Componente principal
const KanbanCardDetailsSection = ({ card, priorities, cardTags, assigneds }) => {
  const emitter = useEvent();
  const KanbanCardId = card?.id;

  // Dados do card sempre vindos da rota — nunca do prop nem de cache
  const [cardData, setCardData] = useState(null);
  const [loadingCard, setLoadingCard] = useState(true);

  const [priorityValue, setPriorityValue] = useState([]);
  const [oldPriorityValue, setOldPriorityValue] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedsUsers, setSelectedsUsers] = useState([]);
  const [users, setUsers] = useState(assigneds);

  const [titleCard, setTitleCard] = useState('');
  const [descriptionCard, setDescriptionCard] = useState('');

  // Ações para renomear titulos
  const [renamingTitle, setRenamingTitle] = useState(false);
  const titleRef = useRef(null);

  // Ações para renomear descrição
  const [renamingDescription, setRenamingDescription] = useState(false);
  const descriptionRef = useRef(null);

  const [openPriorityModal, setOpenPriorityModal] = useState(false);
  const [openTagModal, setOpenTagModal] = useState(false);

  const [loading, setLoading] = useState({
    title: false,
    description: false,
    priority: false,
    tags: false,
    contacts: false,
    tag: false,
    users: false
  });

  // Busca o card 100% atualizado pela rota — sem cache
  const fetchCard = useCallback(async () => {
    if (!KanbanCardId) return;
    try {
      const { data } = await api.get(`/kanban/card/${KanbanCardId}`);
      setCardData(data);
      setTitleCard(data?.title || '');
      setDescriptionCard(data?.description || '');
      const nextPriority = data?.kanbanPriorityLevel ? [data.kanbanPriorityLevel] : [];
      setPriorityValue(nextPriority);
      setOldPriorityValue(nextPriority);
      setSelectedTags(data?.tags || []);
      setSelectedsUsers(data?.assigned || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCard(false);
    }
  }, [KanbanCardId]);

  // Refetch sempre que o id mudar (descarta o estado anterior)
  useEffect(() => {
    setLoadingCard(true);
    setCardData(null);
    fetchCard();
  }, [fetchCard]);

  // Refetch ao receber eventos do card — garante 100% sincronizado
  useEffect(() => {
    if (!KanbanCardId) return;
    const handler = (event) => {
      if (event?.cardId === KanbanCardId || event?.card?.id === KanbanCardId) {
        fetchCard();
      }
    };
    emitter.on('kanban-card', handler);
    return () => emitter.off('kanban-card', handler);
  }, [emitter, KanbanCardId, fetchCard]);

  // Mantém o catálogo de usuários em sincronia com o prop
  useEffect(() => {
    setUsers(assigneds);
  }, [assigneds]);

  // Funções de atualização
  const updatePriorityValue = (values, setFunction) => {
    if (!values || values.length === 0 || (values.length === 1 && values[0] === undefined)) {
      setFunction([]);
    } else {
      setFunction([values[values.length - 1]]);
    }
  };

  const updateTagsValue = (values, setFunction) => {
    if (!values || values.length === 0 || (values.length === 1 && values[0] === undefined)) {
      setFunction([]);
    } else {
      setFunction(values);
    }
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch (error) {
      console.error("Erro ao formatar data:", error);
      return dateString;
    }
  };

  useEffect(() => {
    if (!cardData) return;
    if (
      priorityValue?.length !== oldPriorityValue?.length ||
      (priorityValue?.length > 0 && oldPriorityValue?.length > 0 && priorityValue[0]?.id !== oldPriorityValue[0]?.id)
    ) {
      if (loading.priority) return;

      (async () => {
        try {
          setLoading((prev) => ({ ...prev, priority: true }));

          const { data } = await api.put(`/kanban/card/${KanbanCardId}`, { kanbanPriorityId: priorityValue[0]?.id || null });

          emitter.emit("kanban-card", {
            action: 'update',
            kanbanId: data.kanbanId,
            columnId: data.kanbanColumnId,
            cardId: KanbanCardId,
            card: data
          });

          setOldPriorityValue(priorityValue);
          await fetchCard();
        } catch (err) {
          console.error(err);
        } finally {
          setLoading((prev) => ({ ...prev, priority: false }));
        }
      })();
    }
  }, [priorityValue]);

  const handleUpdateTags = async (values) => {
    if (loading.tag) return;
    try {
      setLoading((prev) => ({ ...prev, tag: true }));
      const { data } = await api.put(`/kanban/card/${KanbanCardId}`, { tagIds: values.map(tag => tag.id), isUpdateTags: true })
      emitter.emit("kanban-card", {
        action: 'update',
        kanbanId: data.kanbanId,
        columnId: data.kanbanColumnId,
        cardId: KanbanCardId,
        card: data
      });
      setSelectedTags(values);
      await fetchCard();
    } catch (err) {
      console.error(err)
    } finally {
      setLoading((prev) => ({ ...prev, tag: false }));
    }
  };

  const handleNewListOfAssigned = async (assignedsUsers) => {
    const oldAssigneds = selectedsUsers;
    setLoading((prev) => ({ ...prev, users: true }));
    try {
      setSelectedsUsers(assignedsUsers);
      const assignedIds = Array.isArray(assignedsUsers) && assignedsUsers.length > 0
        ? assignedsUsers.map(a => a.id)
        : null;
      const { data } = await api.put(`/kanban/assigned/${KanbanCardId}`, { assigned: assignedIds });
      emitter.emit("kanban-card", {
        action: 'update',
        kanbanId: cardData?.kanbanId,
        columnId: cardData?.kanbanColumnId,
        cardId: KanbanCardId,
        card: { ...(cardData || {}), assigned: data || [] }
      });
      await fetchCard();
    } catch (err) {
      console.error(err);
      setSelectedsUsers(oldAssigneds);
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  const handleSaveRenameTitle = async (value) => {
    setLoading((prev) => ({ ...prev, title: true }));
    setRenamingTitle(false);
    try {
      const { data } = await api.put(`/kanban/card/${KanbanCardId}`, { title: value });
      emitter.emit("kanban-card", {
        action: 'update',
        kanbanId: data.kanbanId,
        columnId: data.kanbanColumnId,
        cardId: KanbanCardId,
        card: data
      });
      setTitleCard(data.title);
      await fetchCard();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, title: false }));
    }
  };

  const handleSaveRenameDescription = async (value) => {
    setLoading((prev) => ({ ...prev, description: true }));
    setRenamingDescription(false);
    try {
      const { data } = await api.put(`/kanban/card/${KanbanCardId}`, { description: value });
      emitter.emit("kanban-card", {
        action: 'update',
        kanbanId: data.kanbanId,
        columnId: data.kanbanColumnId,
        cardId: KanbanCardId,
        card: data
      });
      setDescriptionCard(data.description);
      await fetchCard();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, description: false }));
    }
  };

  const handleCreatePriority = () => {
    setOpenPriorityModal(true)
  };

  const handleCreateTag = () => {
    setOpenTagModal(true)
  };

  const creator = cardData?.creator;
  const createdAt = cardData?.createdAt;
  const startedAt = cardData?.startedAt;
  const finishedAt = cardData?.finishedAt;
  const concludedAt = cardData?.concludedAt;

  const hasStarted = !!startedAt;
  const hasFinished = !!finishedAt;
  const hasConcluded = !!concludedAt;
  const isCreationActive = !!createdAt;
  const isStartActive = hasStarted;
  const isFinishActive = hasFinished;
  const isConcludeActive = hasConcluded;

  if (loadingCard && !cardData) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      </Container>
    );
  }

  if (!cardData) {
    return null;
  }

  // Renderização do componente
  return (
    <>
      <Container>
        {/* Título do Card */}
        <EditableField
          ref={titleRef}
          loading={loading.title}
          onClick={(e) => {
            if (loading.title) return;
            e.stopPropagation();
            if (renamingTitle) return;
            setRenamingTitle(true);
          }}
        >
          {renamingTitle ? (
            <KanbanInput
              label="Título"
              limit={1000}
              fontSize={'1.2rem'}
              value={titleCard}
              onSave={handleSaveRenameTitle}
              onCancel={() => setRenamingTitle(false)}
            />
          ) : (
            <TitleContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                <Title
                  variant="h6"
                  fontWeight="bold"
                  whiteSpace="pre-wrap"
                  loading={loading.title}
                >
                  {titleCard}
                </Title>
                <StyledEditIcon className="edit-icon" />
              </Box>
              {loading.title && (
                <CircularProgress size={20} color="primary" />
              )}
            </TitleContainer>
          )}
        </EditableField>

        {/* Seção de autoria e timelines compacta porém estilizada */}
        <TimelineSection>
          {/* Seção de Autoria */}
          <AuthorSection>
            <AuthorAvatar alt={creator.name} src={creator.media} />
            <AuthorInfo>
              <AuthorLabel>Criado por</AuthorLabel>
              <AuthorName>{creator.name}</AuthorName>
            </AuthorInfo>
          </AuthorSection>

          <TimelineTitle>Timeline do Projeto</TimelineTitle>

          {/* Timeline de Status */}
          <TimelineContainer>
            <TimelineConnector />

            {createdAt && (
              <TimelineItem active={isCreationActive} color="primary">
                <TimelineIcon
                  color="primary"
                  active={isCreationActive}
                >
                  <CalendarTodayIcon style={{ fontSize: 18 }} />
                </TimelineIcon>
                <TimelineText>
                  <TimelineLabel>Criado</TimelineLabel>
                  <TimelineValue>{formatDate(createdAt)}</TimelineValue>
                </TimelineText>
              </TimelineItem>
            )}

            {(createdAt || startedAt) && (
              <TimelineItem active={isStartActive} color="warning">
                <TimelineIcon
                  color="warning"
                  active={isStartActive}
                >
                  <PlayArrowIcon style={{ fontSize: 18 }} />
                </TimelineIcon>
                <TimelineText>
                  <TimelineLabel>Iniciado</TimelineLabel>
                  <TimelineValue>
                    {startedAt ? formatDate(startedAt) : 'Pendente'}
                  </TimelineValue>
                </TimelineText>
              </TimelineItem>
            )}

            {(createdAt || startedAt || finishedAt) && (
              <TimelineItem active={isFinishActive} color="success">
                <TimelineIcon
                  color="success"
                  active={isFinishActive}
                >
                  <CheckCircleIcon style={{ fontSize: 18 }} />
                </TimelineIcon>
                <TimelineText>
                  <TimelineLabel>Finalizado</TimelineLabel>
                  <TimelineValue>
                    {finishedAt ? formatDate(finishedAt) : 'Pendente'}
                  </TimelineValue>
                </TimelineText>
              </TimelineItem>
            )}

            {(createdAt || startedAt || finishedAt || concludedAt) && (
              <TimelineItem active={isConcludeActive} color="info">
                <TimelineIcon
                  color="info"
                  active={isConcludeActive}
                >
                  <TaskAltIcon style={{ fontSize: 18 }} />
                </TimelineIcon>
                <TimelineText>
                  <TimelineLabel>Concluído</TimelineLabel>
                  <TimelineValue>
                    {concludedAt ? formatDate(concludedAt) : 'Pendente'}
                  </TimelineValue>
                </TimelineText>
              </TimelineItem>
            )}
          </TimelineContainer>
        </TimelineSection>

        {/* Seção de Contatos */}
        <Section>
          <SectionTitle variant="h6">Contatos & Atribuições</SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <ContactAutoComplete
              placeholder="Solicitante"
              cardId={KanbanCardId}
            />

            <FollowersContactsAutoComplete
              placeholder="Seguidores"
              cardId={KanbanCardId}
            />

            <KanbanUsersAutocomplete
              labels={users}
              placeholder="Usuários Atribuídos"
              search={"Busque para atribuir"}
              setValue={(values) => handleNewListOfAssigned(values)}
              value={selectedsUsers}
              loading={loading.users}
            />
          </Box>
        </Section>

        <CompactDivider />

        {/* Seção de Prioridade e Tags */}
        <Section>
          <SectionTitle variant="h6">Propriedades</SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <KanbanPriorityAutoComplete
              labels={priorities}
              placeholder="Prioridade"
              search={"Prioridade de Tarefa"}
              setValue={(values) => updatePriorityValue(values, setPriorityValue)}
              value={priorityValue}
              loading={loading.priority}
              handleCreate={handleCreatePriority}
            />

            <KanbanTagsNewAutoComplete
              labels={cardTags}
              placeholder="Marcadores"
              search={"Marcadores de Tarefa"}
              setValue={(values) => updateTagsValue(values, handleUpdateTags)}
              value={selectedTags}
              loading={loading.tag}
              handleCreate={handleCreateTag}
            />
          </Box>
        </Section>

        {/* Descrição */}
        <Section>
          <SectionTitle variant="h6">Detalhes</SectionTitle>
          <EditableField
            ref={descriptionRef}
            loading={loading.description}
            onClick={(e) => {
              if (loading.description) return;
              e.stopPropagation();
              if (renamingDescription) return;
              setRenamingDescription(true);
            }}
          >
            <Label>Descrição</Label>
            {renamingDescription ? (
              <KanbanInput
                label="Descrição"
                limit={3000}
                fontSize={'0.9375rem'}
                multiline
                value={descriptionCard}
                onSave={handleSaveRenameDescription}
                onCancel={() => setRenamingDescription(false)}
              />
            ) : (
              <DescriptionBox isEmpty={!descriptionCard}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <DescriptionText
                      empty={!descriptionCard}
                      whiteSpace="pre-wrap"
                    >
                      {descriptionCard || "Adicionar descrição..."}
                    </DescriptionText>
                    <StyledEditIcon className="edit-icon" sx={{ position: 'absolute', right: 12 }} />
                  </Box>
                  {loading.description && (
                    <CircularProgress size={20} color="primary" sx={{ ml: 1, flexShrink: 0 }} />
                  )}
                </Box>
              </DescriptionBox>
            )}
          </EditableField>
        </Section>

        {/* Seção de Comentários */}
        <Section>
          <KanbanComments cardId={KanbanCardId} />
        </Section>

        {/* Modais */}
        <CreateOrEditKanbanPriorityModal
          open={openPriorityModal}
          onClose={setOpenPriorityModal}
          kanbanId={cardData?.kanbanId}
        />

        <CreateOrEditKanbanTagModal
          open={openTagModal}
          onClose={setOpenTagModal}
          kanbanId={cardData?.kanbanId}
        />
      </Container>
    </>
  );
};

export default KanbanCardDetailsSection;